
import { Message } from "@/types/chat";
import { debugLogger } from '@/utils/debugLogger';

/**
 * Format time as HH:MM
 */
export const formatMessageTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/**
 * Format audio recording time as MM:SS
 */
export const formatRecordingTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format relative time for chat messages - CORRIGIDO
 */
export const formatRelativeTime = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const now = new Date();
    const messageDate = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(messageDate.getTime())) {
      debugLogger.error('Invalid date string:', dateString);
      return '';
    }
    
    const diffInMilliseconds = now.getTime() - messageDate.getTime();
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // Menos de 1 minuto
    if (diffInMinutes < 1) {
      return "agora";
    }

    // Menos de 1 hora
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min`;
    }

    // Mesmo dia - mostrar diferença em horas
    if (diffInHours < 24) {
      return `${diffInHours}h`;
    }

    // Ontem
    if (diffInDays === 1) {
      return "ontem";
    }

    // Anteontem
    if (diffInDays === 2) {
      return "anteontem";
    }

    // Mais de 2 dias - mostrar data formatada
    if (diffInDays < 7) {
      return `${diffInDays} dias`;
    }

    // Mais de uma semana - mostrar data
    return messageDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    });
  } catch (error) {
    debugLogger.error('Error formatting relative time:', error, { dateString });
    return '';
  }
};

/**
 * Check if message is from the current user
 */
export const isCurrentUserMessage = (message: Message, userId?: string): boolean => {
  if (!userId) return false;
  return message.sender_id === userId;
};

/**
 * Get fallback image for broken images
 */
export const getImageFallback = (): string => {
  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23e0e0e0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' text-anchor='middle' dominant-baseline='middle' fill='%23666'%3ENão foi possível carregar a imagem%3C/text%3E%3C/svg%3E";
};

/**
 * Format date separator for chat messages
 */
export const formatDateSeparator = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const now = new Date();
    const messageDate = new Date(dateString);
    
    if (isNaN(messageDate.getTime())) {
      return '';
    }
    
    const diffInDays = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (isSameDay(now, messageDate)) {
      return 'Hoje';
    }
    
    if (diffInDays === 1) {
      return 'Ontem';
    }
    
    if (diffInDays === 2) {
      return 'Anteontem';
    }
    
    return messageDate.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long'
    });
  } catch (error) {
    debugLogger.error('Error formatting date separator:', error, { dateString });
    return '';
  }
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Group messages by date
 */
export const groupMessagesByDate = (messages: any[]): { date: string; messages: any[] }[] => {
  const groups: { date: string; messages: any[] }[] = [];
  
  messages.forEach((message) => {
    const messageDate = new Date(message.created_at);
    const dateKey = messageDate.toDateString();
    
    const existingGroup = groups.find(group => group.date === dateKey);
    
    if (existingGroup) {
      existingGroup.messages.push(message);
    } else {
      groups.push({
        date: dateKey,
        messages: [message]
      });
    }
  });
  
  return groups;
};
